import React, { useRef, useState } from 'react';
import { greekLetters, letters, numbers } from "../rosterGrid";
import useOutsideClick from '../../../../utils/use-outside-click';

// Styles
import styles from './../info.module.css';

interface PackContentsTableProps {
  quantities: { [key: string]: number };
  isPrintView?: boolean;
}

const generateCSVContent = (quantities: { [key: string]: number }): string => {
  const csvRows = Object.entries(quantities).map(([character, quantity]) => {
    return `${character},${quantity}`;
  });

  return csvRows.join('\n');
};

const generateCopyContent = (quantities: { [key: string]: number }): string => {
  const copyLines = Object.entries(quantities).map(([character, quantity]) => {
    return `${character}: ${quantity}`;
  });

  return copyLines.join('\n');
};

const downloadCSV = (content: string, filename: string = 'pack-contents.csv') => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const copyToClipboard = async (content: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(content);
    return true;
  } catch (err) {
    console.error('Failed to copy to clipboard:', err);
    return false;
  }
};

export const PackContentsTable: React.FC<PackContentsTableProps> = ({
  quantities,
  isPrintView = false
}) => {
  const [showActions, setShowActions] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const menu = useRef<HTMLDivElement>(null);

  const entries = Object.entries(quantities);

  const getContentTypeHeader = () => {
    const keys = Object.keys(quantities);
    if (keys.length === 0) return "Items";

    const hasNumbers = keys.some(key => numbers.map(String).includes(key));
    const hasLetters = keys.some(key => letters.includes(key));
    const hasGreekLetters = keys.some(key => greekLetters.includes(key));

    if (hasNumbers && !hasLetters && !hasGreekLetters) {
      return "Numbers";
    }

    if (hasGreekLetters && !hasNumbers && !hasLetters) {
      return "Greek Letters";
    }

    if (hasLetters && !hasNumbers && !hasGreekLetters) {
      return "Letters";
    }

    return "Items";
  };

  const handleExportCSV = () => {
    const csvContent = generateCSVContent(quantities);
    if (csvContent) {
      downloadCSV(csvContent, 'pack-contents.csv');
    }
  };

  const handleCopyToClipboard = async () => {
    const copyContent = generateCopyContent(quantities);
    if (copyContent) {
      const success = await copyToClipboard(copyContent);
      if (success) {
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 1500);
      }
    }
  };

  useOutsideClick(menu, () => {
    setShowActions(false);
  });

  const hasExportableData = entries.length > 0;

  return (
    <div className={`flex flex-col ${isPrintView && "text-sm"}`}>
      <div className='flex justify-between items-start pt-[1.5em] pb-0'>
        <h3 className={`uppercase text-[#F36E29] font-bold ${!isPrintView ? 'px-[1.5em]' : ''}`}>
          Pack Contents
        </h3>
        {!isPrintView && hasExportableData && (
          <div className="relative pr-4" ref={menu}>
            <button
              onClick={() => setShowActions(!showActions)}
              className="flex items-center gap-2 px-3 py-1.5 text-[#F36E29] rounded-3xl border border-solid border-[#F36E29]"
              aria-label="Export options"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export
              <svg className={`w-3 h-3 transition-transform duration-200 ${showActions ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {showActions && (
              <div
                className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 min-w-[185px] mr-4">
                <button
                  onClick={handleExportCSV}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-[#f2f2f2]"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download CSV
                </button>
                <button
                  onClick={handleCopyToClipboard}
                  className="flex items-center gap-2 w-full px-4 py-2 text-sm hover:bg-[#f2f2f2] relative"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  Copy to Clipboard
                  {copySuccess && (
                    <span className="absolute right-2 text-sm text-[#F36E29] font-medium">
                      ✓
                    </span>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
      <div className={styles.cardBody}>
        <table className="table-auto w-full">
          <thead>
            <tr className="bg-[#f2f2f2] border-b-[rgb(218,218,218)] border-b border-solid rounded-xl">
              <th className={`px-4 ${!isPrintView ? 'py-2' : 'py-1'} text-left text-[black] font-medium capitalize`}>
                {getContentTypeHeader()}
              </th>
              <th className={`px-4 ${!isPrintView ? 'py-2' : 'py-1'} text-left text-[black] font-medium capitalize`}>
                Quantity
              </th>
            </tr>
          </thead>
          <tbody>
            {entries.map(([character, quantity]) => (
              <tr
                key={character}
                className="border-b-[rgb(218,218,218)] border-b border-solid px-0 py-[0.5em] font-semibold text-[gray]"
              >
                <td className={`px-4 ${!isPrintView ? 'py-2' : 'py-1'}`}>
                  {character}
                </td>
                <td className={`px-4 ${!isPrintView ? 'py-2' : 'py-1'}`}>
                  {quantity}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};